/* eslint-disable @typescript-eslint/no-unused-vars, jsx-a11y/alt-text */
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Calendar, Table, Tag, Modal, Typography, Badge } from "antd";
import React from "react";

import { useIsMobile } from "./hooks/useIsMobile";
import { useS3Data } from "./hooks/useS3Data";
import { EconomicEvent } from "./types";

const EconomicCalendar = () => {
  const isMobile = useIsMobile();
  const [date, setDate] = useState(dayjs());
  const [selectedItem, setSelectedItem] = useState<EconomicEvent | null>(null);
  const [selectedDateItems, setSelectedDateItems] = useState<EconomicEvent[]>([]);

  const s3Key = `data/calendar-${date.format("YYYY.MM")}.json`;
  const { data: rawCalendarItems = [] } = useS3Data<EconomicEvent[]>(s3Key);

  const calendarItems = rawCalendarItems.filter(
    (item) =>
      item.impact === "상" ||
      item.country === "대한민국" ||
      (item.country === "미국" && item.impact === "중")
  );

  const handleTagClick = (item: EconomicEvent) => {
    setSelectedItem(item);
  };

  const handleDateClick = (items: EconomicEvent[]) => {
    setSelectedDateItems(items);
  };

  const handleModalCancel = () => {
    setSelectedItem(null);
    setSelectedDateItems([]);
  };

  const columns = [
    {
      title: "날짜",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "시간",
      dataIndex: "time",
      key: "time",
    },
    {
      title: "중요도",
      dataIndex: "impact",
      key: "impact",
      render: (text: string) => {
        switch (text) {
          case "상":
            return (
              <Tag color="red">
                <b>{text}</b>
              </Tag>
            );
          case "중":
            return (
              <Tag color="orange">
                <b>{text}</b>
              </Tag>
            );
          case "하":
            return (
              <Tag color="blue">
                <b>{text}</b>
              </Tag>
            );
        }
      },
    },
    {
      title: "나라",
      dataIndex: "country",
      key: "country",
      width: 120,
      render: (text: string) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <img
              src={getCountryFlag(text)}
              style={{
                width: 20,
              }}
            />
            <Typography>
              <b>{text}</b>
            </Typography>
          </div>
        );
      },
    },
    {
      title: "경제 지표 캘린더",
      dataIndex: "event",
      key: "event",
      width: 200,
    },
    {
      title: "실제",
      dataIndex: "actual",
      key: "actual",
    },
    {
      title: "예상",
      dataIndex: "forecast",
      key: "forecast",
    },
    {
      title: "이전",
      dataIndex: "previous",
      key: "previous",
    },
  ];

  const getCountryFlag = (country: string) => {
    switch (country) {
      case "유럽연합":
        return "https://i.namu.wiki/i/-aDCVSLRt5Gn7KGaXNZyjNVxjRey1gnfmg_16JrK7bqY1ihROnM1YVkzE1Da-FZWCp6JORbIBfVSSUIRjq0XfA.svg";
      case "이탈리아":
        return "https://www.worldometers.info//img/flags/small/tn_it-flag.gif";
      case "오스트레일리아":
        return "https://www.worldometers.info//img/flags/small/tn_as-flag.gif";
      case "독일":
        return "https://www.worldometers.info//img/flags/small/tn_gm-flag.gif";
      case "캐나다":
        return "https://www.worldometers.info//img/flags/small/tn_ca-flag.gif";
      case "대한민국":
        return "https://www.worldometers.info//img/flags/small/tn_ks-flag.gif";
      case "영국":
        return "https://www.worldometers.info//img/flags/small/tn_uk-flag.gif";
      case "일본":
        return "https://www.worldometers.info//img/flags/small/tn_ja-flag.gif";
      case "멕시코":
        return "https://www.worldometers.info//img/flags/small/mx-flag.gif";
      case "미국":
        return "https://www.worldometers.info//img/flags/small/tn_us-flag.gif";
      case "중국":
        return "https://www.worldometers.info//img/flags/small/tn_ch-flag.gif";
    }
  };

  return (
    <div>
      <Calendar
        onChange={(date) => {
          setDate(date);
        }}
        cellRender={(value) => {
          const date = value.format("YYYY.MM.DD");
          const items = calendarItems.filter((item) => item.date === date);
          const eventCount = items.length; // 해당 날짜의 이벤트 개수
          return (
            <div onClick={() => handleDateClick(items)}>
              {/* 해당 날짜의 이벤트 개수를 Badge로 표시 */}
              {eventCount > 0 && (
                <Badge
                  styles={{
                    root: {
                      position: "absolute",
                      left: 10,
                      top: 0,
                    },
                  }}
                  count={eventCount}
                  style={{
                    backgroundColor: "#1677ff",
                    marginTop: 5,
                  }}
                />
              )}
              {items.map((item, index) => (
                <Tag key={index} onClick={() => handleTagClick(item)}>
                  <img
                    src={getCountryFlag(item.country)}
                    style={{
                      width: 10,
                      marginRight: 5,
                    }}
                  />
                  <b
                    style={{
                      fontSize: 10,
                    }}
                  >
                    {item.event}
                  </b>
                  <br />
                  {item.time}
                </Tag>
              ))}
            </div>
          );
        }}
      />
      {/* Modal for single item */}
      {selectedItem && (
        <Modal
          width={800}
          title={selectedItem.event}
          visible={true}
          onCancel={handleModalCancel}
          footer={null}
        >
          <Table
            dataSource={[selectedItem]}
            columns={columns}
            pagination={false}
            rowKey="event"
          />
        </Modal>
      )}
      {/* Modal for full list of events */}
      {selectedDateItems.length > 0 && (
        <Modal
          width={"90vw"}
          title={`${selectedDateItems[0]?.date} Events`}
          visible={true}
          onCancel={handleModalCancel}
          footer={null}
          centered
        >
          <Table
            dataSource={selectedDateItems}
            columns={columns}
            pagination={false}
            scroll={{ y: isMobile ? "50vh" : "70vh" }}
            rowKey="event"
          />
        </Modal>
      )}
    </div>
  );
};

export default EconomicCalendar;
